/* eslint-disable no-nested-ternary */
import useCases from '@brands/hooks/useCases';
import useDebounce from '@brands/hooks/useDebounce';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useSubscribe, { Subscriber } from '@brands/hooks/useSubscribe';
import { CaseType, ICase, Status } from '@brands/services/cases/types/ICase';
import { listAllClientTags } from '@brands/services/identity/listAllClientTags';
import { getClientDivisionOptions } from '@brands/services/identity/clientDivisionOptions';
import { listAllOrganizations } from '@brands/services/identity/listAllOrganizations';
import { listInsuranceCarriers } from '@brands/services/identity/listInsuranceCarriers';
import { ClientTag, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { ColumnVisibilityKey } from '@brands/store/slices/columnVisibilitySlice';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { formatDateParts } from '@brands/Utils/formatDate';
import { getStatusColorStyles } from '@brands/Utils/getColorForStatus';
import { getFriendlyName } from '@brands/Utils/getFriendlyName';
import { getTextForStatus } from '@brands/Utils/getTextForStatus';
import {
  allCaseTypeOptions,
  caseStatusOptions,
  patientTypeOptions,
  paymentMethodOptions,
  stateOptions,
} from '@brands/Utils/selectOptions';
import {
  getSessionNumber,
  getSessionString,
  readSessionTableState,
  writeSessionTableState,
} from '@brands/Utils/sessionTableState';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, TextField } from '@mui/material';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { useLocation, useSearchParams } from 'react-router-dom';

import MinimalDateRangePicker from '@brands/Components/MinimalDateRangePicker/MinimalDateRangePicker';
import { usePersistedRowsPerPage } from '../../../hooks/usePersistedRowsPerPage';
import CADataGrid, { ColumnDef, caGridStyles } from '../CareAdvisor/Components/CADataGrid';
import ProfileComponent from '../CareAdvisor/Component/ProfileComponent';
import styles from './styles.module.scss';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';

type Option = { value: string; label: string; id?: string };

const filterLabelSx = { fontSize: 12, fontWeight: 400 };

const SORT_KEY_MAP: Record<string, string> = {
  id: 'case_id',
  status: 'status',
  type: 'type',
  video_conference_date: 'scheduled_at',
  completed_at: 'completed_at',
  canceled_at: 'canceled_at',
  patient_current_state: 'patient_current_state',
  patient_role: 'patient_user_type',
  markers: 'client_tag',
  patient: 'patient_full_name',
  provider: 'provider_full_name',
  care_advisor: 'care_advisor_full_name',
  collector: 'collector_full_name',
  payment_method: 'payment_method',
  patient_self_pay_cost: 'patient_self_pay_cost',
  insurance_carrier: 'insurance_carrier',
};

// Static column widths derived from content expectations
const COL_WIDTHS = {
  id: 120,
  status: 180,
  type: 165,
  video_conference_date: 175,
  completed_at: 200,
  canceled_at: 195,
  patient_current_state: 150,
  patient_role: 215,
  markers: 165,
  patient: 175,
  provider: 175,
  care_advisor: 175,
  collector: 220,
  payment_method: 195,
  patient_self_pay_cost: 175,
  insurance_carrier: 165,
};

const DEFAULT_CASE_STATUSES = [
  Status.Upcoming,
  Status.AcceptedCareAdvisor,
  Status.AcceptedProvider,
  Status.InProgressProvider,
  Status.InProgressCareAdvisor,
  Status.CompletedByProvider,
];

const SESSION_STATE_KEY = 'dentistry-one:admin:cases';

const CasesList = (): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const location = useLocation();
  const roleNavigate = useRoleNavigate();
  const savedState = useMemo(() => readSessionTableState(SESSION_STATE_KEY), []);
  const rowsPerPageHydratedRef = useRef(false);

  // "View full history" on the consultation page deep-links here with a
  // ?patient_full_name= query param. Seed the patient filter from it.
  const [searchParams] = useSearchParams();
  const initialPatientFilter = searchParams.get('patient_full_name')?.trim() ?? '';
  const incomingPatientPersistedRef = useRef(false);

  const [filtersReady, setFiltersReady] = useState(false);
  const timezone = moment.tz.guess();
  const [clientTagOptions, setClientTagOptions] = useState<Option[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<Option[]>([]);
  const [insurancePlanName, setInsurancePlanName] = useState<Option[]>([]);

  const [showFilterInputs, setShowFilterInputs] = useState(true);
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>(() => {
    const { page, rowsPerPage: _rowsPerPage, sort_by, sort_direction, ...filters } = savedState;
    return initialPatientFilter ? { ...filters, patient_full_name: initialPatientFilter } : filters;
  });

  const [selectedRangeConsultationDate, setSelectedRangeConsultationDate] = useState<[Date, Date] | null>(null);
  const [selectedRangeCompletedDate, setSelectedRangeCompletedDate] = useState<[Date, Date] | null>(null);
  const [selectedRangeCanceledDate, setSelectedRangeCanceledDate] = useState<[Date, Date] | null>(null);

  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(TablePaginationKey.SuperAdminCases, 10);
  const [targetPageIndex, setTargetPageIndex] = useState(0);
  const [sortColKey, setSortColKey] = useState<string>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const isAdminRole = [UserRoleName.SuperAdmin, UserRoleName.OrganizationAdmin].includes(
    userInfo.role.name as UserRoleName,
  );

  const canCreateCase = isAdminRole;

  useEffect(() => {
    if (rowsPerPageHydratedRef.current) return;
    rowsPerPageHydratedRef.current = true;
    const rowsPerPageFromSession = getSessionNumber(savedState, 'rowsPerPage', rowsPerPage);
    const pageFromSession = getSessionNumber(savedState, 'page', 1);
    const sortByFromSession = getSessionString(savedState, 'sort_by');
    const sortDirectionFromSession = getSessionString(savedState, 'sort_direction');
    setTargetPageIndex(initialPatientFilter ? 0 : pageFromSession > 0 ? pageFromSession - 1 : 0);
    if (rowsPerPageFromSession > 0 && rowsPerPageFromSession !== rowsPerPage) {
      setRowsPerPage(rowsPerPageFromSession);
    }
    setSortColKey(SORT_KEY_MAP[sortByFromSession] ? sortByFromSession : 'id');
    setSortDirection(
      sortDirectionFromSession === 'asc' || sortDirectionFromSession === 'desc' ? sortDirectionFromSession : 'desc',
    );
    if (Object.keys(savedState).length > 0 || initialPatientFilter) setShowFilterInputs(true);
    setFiltersReady(true);

    if (initialPatientFilter && !incomingPatientPersistedRef.current) {
      incomingPatientPersistedRef.current = true;
      writeSessionTableState(SESSION_STATE_KEY, {
        ...savedState,
        patient_full_name: initialPatientFilter,
        page: 1,
        rowsPerPage: rowsPerPageFromSession > 0 ? rowsPerPageFromSession : rowsPerPage,
      });
    }

    const parseLocalDateString = (value: unknown): Date | null => {
      if (typeof value !== 'string') return null;
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    const hydrateRange = (
      key: 'consultation_date' | 'completed_date' | 'canceled_date',
      setter: React.Dispatch<React.SetStateAction<[Date, Date] | null>>,
    ) => {
      const start = parseLocalDateString(savedState[`${key}_start`]);
      const end = parseLocalDateString(savedState[`${key}_end`]);
      if (start && end) setter([start, end]);
      else setter(null);
    };

    hydrateRange('consultation_date', setSelectedRangeConsultationDate);
    hydrateRange('completed_date', setSelectedRangeCompletedDate);
    hydrateRange('canceled_date', setSelectedRangeCanceledDate);
  }, [initialPatientFilter, rowsPerPage, savedState, setRowsPerPage]);

  const fetchOrganizationOptions = async (): Promise<void> => {
    try {
      const data = await listAllOrganizations();
      if (userInfo.role.name === UserRoleName.OrganizationAdmin) {
        data.nodes = data.nodes.filter((org) => org.id === userInfo.organizations[0].id);
      }
      setOrganizationOptions(data.nodes.map((o) => ({ value: o.id, label: o.name, id: o.id.toString() })));
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  const fetchClientTagsOptions = async (): Promise<void> => {
    try {
      const data = await listAllClientTags();
      setClientTagOptions(
        data.nodes.map((ct) => ({ value: ct.code as string, label: ct.name as string, id: ct.id.toString() })),
      );
      setAllClientTags(data.nodes);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  const fetchInsurancePlanOptions = async (): Promise<void> => {
    try {
      const data = await listInsuranceCarriers();
      setInsurancePlanName(data.map((ip) => ({ id: ip.id.toString(), label: ip.name, value: ip.id.toString() })));
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    fetchClientTagsOptions();
    fetchOrganizationOptions();
    fetchInsurancePlanOptions();
  }, []);

  const debouncedFilterValues = useDebounce(filterValues, 500);

  const selectedClientTagIdsForDivision = useMemo(() => {
    const selectedCodes = (filterValues.client_tag as string[] | undefined) || [];
    return allClientTags.filter((t) => selectedCodes.includes(t.code || '')).map((t) => t.id);
  }, [allClientTags, filterValues.client_tag]);

  const divisionFilterOptions = useMemo(
    () => getClientDivisionOptions(allClientTags, selectedClientTagIdsForDivision),
    [allClientTags, selectedClientTagIdsForDivision],
  );

  const params = useMemo(() => {
    const baseParams = {
      sort_by: SORT_KEY_MAP[sortColKey] ?? 'case_id',
      sort_direction: sortDirection,
      limit: rowsPerPage,
      include_patient_care_advisor_conversation: false,
      include_patient_provider_conversation: false,
      include_provider_care_advisor_conversation: false,
      status: DEFAULT_CASE_STATUSES,
    };

    const { sort_by, sort_direction, ...filters } = debouncedFilterValues;
    const merged: any = { ...filters };
    if ('case_status' in merged) {
      merged.status = merged.case_status;
      delete merged.case_status;
    }

    return { ...baseParams, ...merged };
  }, [debouncedFilterValues, rowsPerPage, sortColKey, sortDirection]);

  const { cases, reloadAll, dataLoading, loading, totalNewCases, loadNextPage, loadPreviousPage, pageIndex } = useCases(
    params,
    filtersReady,
    userInfo?.role?.name,
  );

  const commitFilters = (raw: Record<string, any>) => {
    const cleaned: Record<string, any> = { ...raw };
    Object.keys(cleaned).forEach((k) => {
      const v = cleaned[k];
      if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) delete cleaned[k];
    });
    writeSessionTableState(SESSION_STATE_KEY, {
      ...cleaned,
      page: 1,
      rowsPerPage,
      sort_by: sortColKey,
      sort_direction: sortDirection,
    });
    setFilterValues(cleaned);
    setTargetPageIndex(0);
  };

  const handleFilterChange = (key: string, value: any) => commitFilters({ ...filterValues, [key]: value });

  const handleDateRangeFilterChange = (key: string, value: [Date, Date] | null): void => {
    const updated = { ...filterValues };
    if (value?.[0] && value?.[1]) {
      updated[`${key}_start`] = moment.tz(value[0], timezone).startOf('day').utc().toISOString();
      updated[`${key}_end`] = moment.tz(value[1], timezone).endOf('day').utc().toISOString();
    } else {
      delete updated[`${key}_start`];
      delete updated[`${key}_end`];
    }
    switch (key) {
      case 'consultation_date':
        setSelectedRangeConsultationDate(value);
        break;
      case 'completed_date':
        setSelectedRangeCompletedDate(value);
        break;
      case 'canceled_date':
        setSelectedRangeCanceledDate(value);
        break;
    }
    commitFilters(updated);
  };

  const handleSort = useCallback(
    (key: string) => {
      if (!SORT_KEY_MAP[key]) return;
      const nextSortDirection = sortColKey === key ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
      if (sortColKey === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColKey(key);
        setSortDirection('asc');
      }
      setTargetPageIndex(0);
      writeSessionTableState(SESSION_STATE_KEY, {
        ...filterValues,
        page: 1,
        rowsPerPage,
        sort_by: key,
        sort_direction: nextSortDirection,
      });
    },
    [filterValues, rowsPerPage, sortColKey, sortDirection],
  );

  const handlePageChange = useCallback(
    async (nextPage: number): Promise<void> => {
      if (nextPage > pageIndex) {
        await loadNextPage();
      } else if (nextPage < pageIndex) {
        await loadPreviousPage();
      }
      setTargetPageIndex(nextPage);
      writeSessionTableState(SESSION_STATE_KEY, {
        ...filterValues,
        page: nextPage + 1,
        rowsPerPage,
        sort_by: sortColKey,
        sort_direction: sortDirection,
      });
    },
    [filterValues, loadNextPage, loadPreviousPage, pageIndex, rowsPerPage, sortColKey, sortDirection],
  );

  useEffect(() => {
    if (!filtersReady || dataLoading || loading || pageIndex >= targetPageIndex) return;
    void loadNextPage();
  }, [dataLoading, filtersReady, loadNextPage, loading, pageIndex, targetPageIndex]);

  const subscriber: Subscriber = useMemo(
    () => ({
      onMessage: (message: Record<string, any>) => {
        if (['case_updated', 'case_cancelled', 'case_new_message_created', 'case_created'].includes(message.command)) {
          reloadAll();
        }
      },
      commands: ['case_created', 'case_updated', 'case_cancelled', 'case_new_message_created'],
    }),
    [reloadAll],
  );
  const { subscribe, unsubscribe } = useSubscribe();
  useEffect(() => {
    const subscriptionId = subscribe(subscriber);
    return () => unsubscribe(subscriptionId);
  }, [subscribe, unsubscribe, subscriber]);

  const handleRowClick = useCallback(
    (caseRow: ICase) => {
      sessionStorage.setItem('lastNonConsultationPagePath', `${location.pathname}${location.hash}`);
      if (caseRow.type !== CaseType.smile_scan) {
        roleNavigate(`/consultation-page/${caseRow.id}`);
      } else if (caseRow.status === Status.Completed) {
        roleNavigate(`/smile-scan-report/${caseRow.id}`);
      }
    },
    [location.hash, location.pathname, roleNavigate],
  );

  const columns: ColumnDef<ICase>[] = useMemo(
    () => [
      {
        key: 'id',
        label: 'Case ID',
        widthPx: COL_WIDTHS.id,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.id}</span>,
        filterContent: showFilterInputs ? (
          <TextField
            variant="standard"
            fullWidth
            label="Case ID"
            value={filterValues.case_id || ''}
            onChange={(e) => handleFilterChange('case_id', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.case_id), sx: filterLabelSx }}
          />
        ) : undefined,
      },
      {
        key: 'status',
        label: 'Status',
        widthPx: COL_WIDTHS.status,
        renderCell: (row) => (
          <span className={caGridStyles.statusChip} style={getStatusColorStyles(row.status)}>
            {getTextForStatus(userInfo.role.name, row.type, row.status)}
          </span>
        ),
        filterContent: showFilterInputs ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.case_status as string[]) || []).length > 0} sx={filterLabelSx}>
              Status
            </InputLabel>
            <Select
              multiple
              value={(filterValues.case_status as string[]) || []}
              onChange={(e) => handleFilterChange('case_status', e.target.value as string[])}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => caseStatusOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {caseStatusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox size="small" checked={((filterValues.case_status as string[]) || []).includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : undefined,
      },
      {
        key: 'type',
        label: 'Type',
        widthPx: COL_WIDTHS.type,
        renderCell: (row) => <span className={caGridStyles.cellText}>{getFriendlyName(row.type)}</span>,
        filterContent: showFilterInputs ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.case_type as string[]) || []).length > 0} sx={filterLabelSx}>
              Type
            </InputLabel>
            <Select
              multiple
              value={(filterValues.case_type as string[]) || []}
              onChange={(e) => handleFilterChange('case_type', e.target.value as string[])}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => allCaseTypeOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {allCaseTypeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox size="small" checked={((filterValues.case_type as string[]) || []).includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : undefined,
      },
      {
        key: 'video_conference_date',
        label: 'Consult date',
        widthPx: COL_WIDTHS.video_conference_date,
        renderCell: (row) => {
          if (
            row.video_conference_date &&
            [CaseType.phone_call_scheduled, CaseType.oral_health_coaching, CaseType.video_call_scheduled].includes(
              row.type,
            )
          ) {
            const parts = formatDateParts(row.video_conference_date);
            return (
              <span
                className={caGridStyles.cellText}
                style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}
              >
                <span>{parts.date}</span>
                <span style={{ color: 'rgba(13, 32, 53, 0.6)' }}>{parts.time}</span>
              </span>
            );
          }
          return <span className={caGridStyles.cellText}>N/A</span>;
        },
        filterContent: showFilterInputs ? (
          <MinimalDateRangePicker
            handleDateRangeFilterChange={handleDateRangeFilterChange}
            filterKey="consultation_date"
            selectedRange={selectedRangeConsultationDate}
            placeholder="Consult date"
            width="100%"
          />
        ) : undefined,
      },
      {
        key: 'completed_at',
        label: 'Completed date',
        widthPx: COL_WIDTHS.completed_at,
        renderCell: (row) => {
          if (row.status === Status.Completed && row.completed_at) {
            const parts = formatDateParts(row.completed_at);
            return (
              <span
                className={caGridStyles.cellText}
                style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}
              >
                <span>{parts.date}</span>
                <span style={{ color: 'rgba(13, 32, 53, 0.6)' }}>{parts.time}</span>
              </span>
            );
          }
          return <span className={caGridStyles.cellText}>N/A</span>;
        },
        filterContent: showFilterInputs ? (
          <MinimalDateRangePicker
            handleDateRangeFilterChange={handleDateRangeFilterChange}
            filterKey="completed_date"
            selectedRange={selectedRangeCompletedDate}
            placeholder="Completed date"
            width="100%"
          />
        ) : undefined,
      },
      {
        key: 'canceled_at',
        label: 'Canceled date',
        widthPx: COL_WIDTHS.canceled_at,
        renderCell: (row) => {
          if (row.status === Status.Canceled && row.canceled_at) {
            const parts = formatDateParts(row.canceled_at);
            return (
              <span
                className={caGridStyles.cellText}
                style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}
              >
                <span>{parts.date}</span>
                <span style={{ color: 'rgba(13, 32, 53, 0.6)' }}>{parts.time}</span>
              </span>
            );
          }
          return <span className={caGridStyles.cellText}>N/A</span>;
        },
        filterContent: showFilterInputs ? (
          <MinimalDateRangePicker
            handleDateRangeFilterChange={handleDateRangeFilterChange}
            filterKey="canceled_date"
            selectedRange={selectedRangeCanceledDate}
            placeholder="Canceled date"
            width="100%"
          />
        ) : undefined,
      },
      {
        key: 'patient_current_state',
        label: 'Case state',
        widthPx: COL_WIDTHS.patient_current_state,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.patient_current_state}</span>,
        filterContent: showFilterInputs ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.case_state as string[]) || []).length > 0} sx={filterLabelSx}>
              Case state
            </InputLabel>
            <Select
              multiple
              value={(filterValues.case_state as string[]) || []}
              onChange={(e) => handleFilterChange('case_state', e.target.value as string[])}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => stateOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {stateOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox size="small" checked={((filterValues.case_state as string[]) || []).includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : undefined,
      },
      {
        key: 'patient_role',
        label: 'Patient user type',
        widthPx: COL_WIDTHS.patient_role,
        renderCell: (row) => (
          <span className={caGridStyles.cellText}>{row?.patient?.guardian_id === null ? 'Patient' : 'Dependent'}</span>
        ),
        filterContent: showFilterInputs ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.patient_user_type as string[]) || []).length > 0} sx={filterLabelSx}>
              Patient user type
            </InputLabel>
            <Select
              multiple
              value={(filterValues.patient_user_type as string[]) || []}
              onChange={(e) => handleFilterChange('patient_user_type', e.target.value as string[])}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => patientTypeOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {patientTypeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox
                    size="small"
                    checked={((filterValues.patient_user_type as string[]) || []).includes(o.value)}
                  />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : undefined,
      },
      {
        key: 'markers',
        label: 'Client tags',
        widthPx: COL_WIDTHS.markers,
        renderCell: (row) => (
          <span className={caGridStyles.cellText}>
            {row.organization.id === 'dentistry-one' ? row.client?.name || '' : ''}
          </span>
        ),
        filterContent: showFilterInputs ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.client_tag as string[]) || []).length > 0} sx={filterLabelSx}>
              Client tags
            </InputLabel>
            <Select
              multiple
              value={(filterValues.client_tag as string[]) || []}
              onChange={(e) =>
                commitFilters({ ...filterValues, client_tag: e.target.value as string[], client_division_ids: undefined })
              }
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => clientTagOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {clientTagOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox size="small" checked={((filterValues.client_tag as string[]) || []).includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : undefined,
      },
      {
        key: 'client_division',
        label: 'Division',
        widthPx: COL_WIDTHS.markers,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.client_division?.name || ''}</span>,
        filterContent: showFilterInputs ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.client_division_ids as string[]) || []).length > 0} sx={filterLabelSx}>
              {(filterValues.client_tag as string[])?.length ? 'Division' : 'Select a client tag first'}
            </InputLabel>
            <Select
              multiple
              disabled={!(filterValues.client_tag as string[])?.length}
              value={(filterValues.client_division_ids as string[]) || []}
              onChange={(e) => handleFilterChange('client_division_ids', e.target.value as string[])}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => divisionFilterOptions.find((o) => o.id === v)?.label).join(', ')
              }
            >
              {divisionFilterOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  <Checkbox
                    size="small"
                    checked={((filterValues.client_division_ids as string[]) || []).includes(o.id)}
                  />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : undefined,
      },
      {
        key: 'patient',
        label: 'Patient',
        widthPx: COL_WIDTHS.patient,
        renderCell: (row) => <ProfileComponent caseRow={row} role="Patient" showImage={false} />,
        filterContent: showFilterInputs ? (
          <TextField
            variant="standard"
            fullWidth
            label="Patient name"
            value={filterValues.patient_full_name || ''}
            onChange={(e) => handleFilterChange('patient_full_name', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.patient_full_name), sx: filterLabelSx }}
          />
        ) : undefined,
      },
      {
        key: 'provider',
        label: 'Provider',
        widthPx: COL_WIDTHS.provider,
        renderCell: (row) => <ProfileComponent caseRow={row} role="Provider" showImage={false} />,
        filterContent: showFilterInputs ? (
          <TextField
            variant="standard"
            fullWidth
            label="Provider name"
            value={filterValues.provider_full_name || ''}
            onChange={(e) => handleFilterChange('provider_full_name', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.provider_full_name), sx: filterLabelSx }}
          />
        ) : undefined,
      },
      {
        key: 'care_advisor',
        label: 'Care advisor',
        widthPx: COL_WIDTHS.care_advisor,
        renderCell: (row) => <ProfileComponent caseRow={row} role="CA" showImage={false} />,
        filterContent: showFilterInputs ? (
          <TextField
            variant="standard"
            fullWidth
            label="Care advisor name"
            value={filterValues.care_advisor_full_name_filter || ''}
            onChange={(e) => handleFilterChange('care_advisor_full_name_filter', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.care_advisor_full_name_filter), sx: filterLabelSx }}
          />
        ) : undefined,
      },
      {
        key: 'collector',
        label: 'Collector',
        widthPx: COL_WIDTHS.collector,
        renderCell: (row) => <span className={caGridStyles.cellText}>{(row as any).collector?.full_name || ''}</span>,
        filterContent: showFilterInputs ? (
          <TextField
            variant="standard"
            fullWidth
            label="Collector name"
            value={filterValues.collector_full_name || ''}
            onChange={(e) => handleFilterChange('collector_full_name', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.collector_full_name), sx: filterLabelSx }}
          />
        ) : undefined,
      },
      {
        key: 'payment_method',
        label: 'Payment method',
        widthPx: COL_WIDTHS.payment_method,
        renderCell: (row) => {
          if (row.paid_with_credits) return <span className={caGridStyles.cellText}>Plan credits</span>;
          if (row.plan_discount_applied_percent && row.plan_discount_applied_percent > 0) {
            return (
              <span className={caGridStyles.cellText}>{`Plan discount (${row.plan_discount_applied_percent}%)`}</span>
            );
          }
          if (row.pricing?.amount && row.pricing.amount > 0) {
            return <span className={caGridStyles.cellText}>{row.insurance_id ? 'Insurance' : 'Self pay'}</span>;
          }
          return <span className={caGridStyles.cellText}>Free case</span>;
        },
        filterContent: showFilterInputs ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.payment_method as string[]) || []).length > 0} sx={filterLabelSx}>
              Payment method
            </InputLabel>
            <Select
              multiple
              value={(filterValues.payment_method as string[]) || []}
              onChange={(e) => handleFilterChange('payment_method', e.target.value as string[])}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => paymentMethodOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {paymentMethodOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox
                    size="small"
                    checked={((filterValues.payment_method as string[]) || []).includes(o.value)}
                  />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : undefined,
      },
      {
        key: 'patient_self_pay_cost',
        label: 'Self pay cost',
        widthPx: COL_WIDTHS.patient_self_pay_cost,
        renderCell: (row) => {
          if (row.paid_with_credits) return <span className={caGridStyles.cellText}>$0</span>;
          if (
            row.plan_discount_applied_percent &&
            row.plan_discount_applied_percent > 0 &&
            row.pricing?.amount &&
            row.pricing.amount > 0
          ) {
            const discountedCents = Math.round(row.pricing.amount * (1 - row.plan_discount_applied_percent / 100));
            return <span className={caGridStyles.cellText}>{`$${(discountedCents / 100).toFixed(2)}`}</span>;
          }
          if (row.pricing?.amount && row.pricing.amount > 0) {
            if (row.insurance_id !== null) return <span className={caGridStyles.cellText}>N/A</span>;
            const priceInDollars = (row.pricing.amount || 0) / 100;
            return (
              <span className={caGridStyles.cellText}>
                {priceInDollars === 0 ? '' : `$${priceInDollars.toFixed(2)}`}
              </span>
            );
          }
          return <span className={caGridStyles.cellText}>$0</span>;
        },
        filterContent: showFilterInputs ? (
          <TextField
            variant="standard"
            fullWidth
            label="Self pay cost"
            value={filterValues.self_pay_cost || ''}
            onChange={(e) => handleFilterChange('self_pay_cost', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.self_pay_cost), sx: filterLabelSx }}
          />
        ) : undefined,
      },
      {
        key: 'insurance_carrier',
        label: 'Insurance',
        widthPx: COL_WIDTHS.insurance_carrier,
        renderCell: (row) => (
          <span className={caGridStyles.cellText}>
            {row.pricing?.amount && row.pricing.amount > 0
              ? row.insurance_id !== null
                ? (row as any).insurance?.insurance_membership?.insurance_carrier?.name
                : 'N/A'
              : 'N/A'}
          </span>
        ),
        filterContent: showFilterInputs ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.insurance_carrier as string[]) || []).length > 0} sx={filterLabelSx}>
              Insurance
            </InputLabel>
            <Select
              multiple
              value={(filterValues.insurance_carrier as string[]) || []}
              onChange={(e) => handleFilterChange('insurance_carrier', e.target.value as string[])}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => insurancePlanName.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {insurancePlanName.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox
                    size="small"
                    checked={((filterValues.insurance_carrier as string[]) || []).includes(o.value)}
                  />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : undefined,
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [
      showFilterInputs,
      filterValues,
      userInfo.role.name,
      clientTagOptions,
      divisionFilterOptions,
      organizationOptions,
      insurancePlanName,
      selectedRangeConsultationDate,
      selectedRangeCompletedDate,
      selectedRangeCanceledDate,
    ],
  );

  return (
    <div className={styles.casesPageContainer}>
      <div className={styles.casesPageHeader}>
        <div className={styles.casesPageHeaderLeft}>
          {/* Page heading — an h1 in the design, as the other roles' Cases screens are. */}
          <h1 className={styles.sectionTitle}>Cases</h1>
        </div>
        {canCreateCase && (
          <button
            type="button"
            className={styles.createCaseBtn}
            onClick={() => roleNavigate('/review-profile/patient/select')}
          >
            <FiPlus size={16} />
            <span>Create new case</span>
          </button>
        )}
      </div>
      <div className={styles.casesGridWrapper}>
        <CADataGrid
          columns={columns}
          rows={cases}
          onRowClick={handleRowClick}
          count={totalNewCases}
          page={pageIndex}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 100]}
          onPageChange={(p) => {
            void handlePageChange(p);
          }}
          onRowsPerPageChange={(rpp) => {
            setRowsPerPage(rpp);
            setTargetPageIndex(0);
            writeSessionTableState(SESSION_STATE_KEY, {
              ...filterValues,
              page: 1,
              rowsPerPage: rpp,
              sort_by: sortColKey,
              sort_direction: sortDirection,
            });
          }}
          loading={dataLoading || loading}
          sortBy={sortColKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          showManageColumns
          columnVisibilityKey={ColumnVisibilityKey.SuperAdminCases}
        />
      </div>
    </div>
  );
};

export default CasesList;
